import React, { useRef } from "react";

/**
 * Button Component (Signal)
 *
 * Primary = ice-cyan fill with dark ink, secondary = glass fill + line, ghost = text only,
 * danger = sg-danger fill. Legacy variant names map onto those kinds so
 * existing call sites keep working. Styles live in styles/signal-components.css.
 */
const VARIANT_KIND = {
  primary: "primary",
  "gradient-primary": "primary",
  "glass-gradient": "primary",
  secondary: "secondary",
  glass: "secondary",
  info: "secondary",
  "gradient-ocean": "secondary",
  outline: "outline",
  ghost: "ghost",
  link: "link",
  danger: "danger",
  success: "success",
  warning: "warning",
  "gradient-sunset": "warning",
};

const SIZE_KIND = { xs: "xs", sm: "sm", md: "md", lg: "lg", xl: "xl" };

const Button = React.memo(
  ({
    children,
    variant = "primary",
    size = "md",
    type = "button",
    className = "",
    disabled = false,
    onClick,
    isLoading = false,
    leftIcon = null,
    rightIcon = null,
    // Legacy props - will be deprecated
    icon = null,
    iconPosition = "left",
    fullWidth = false,
    ariaLabel,
    // Accepted for compatibility; Signal has no pulse, magnetic or shadow effects.
    // eslint-disable-next-line no-unused-vars
    pulse = false,
    // eslint-disable-next-line no-unused-vars
    magnetic = false,
    // StandardButton compatibility props
    // eslint-disable-next-line no-unused-vars
    rounded = "md",
    // eslint-disable-next-line no-unused-vars
    shadow = true,
    active = false,
    href,
    target,
    rel,
    ariaPressed,
    ariaExpanded,
    loadingText,
    // "idle" | "loading" | "success" | "error" (hooks/useAsyncStatus).
    // "success" morphs the label into a drawn check for its 600 ms hold.
    status,
    successText = "Done",
    ...props
  }) => {
    const buttonRef = useRef(null);
    const loading = isLoading || status === "loading";
    const succeeded = !loading && status === "success";

    // Enhanced click handler
    const handleClick = (event) => {
      if (onClick && !disabled && !loading && !succeeded) {
        onClick(event);
      }
    };

    // Resolve icon props - prefer the new leftIcon/rightIcon pattern but support legacy pattern
    const resolvedLeftIcon =
      leftIcon ||
      (icon && (iconPosition === "left" || !iconPosition) ? icon : null);
    const resolvedRightIcon =
      rightIcon || (icon && iconPosition === "right" ? icon : null);

    // Check if this is an icon-only button for accessibility
    const isIconOnly =
      (!children || children === "") && (resolvedLeftIcon || resolvedRightIcon);
    const accessibilityProps = isIconOnly
      ? { "aria-label": ariaLabel || "Button" }
      : {};

    const buttonClasses = [
      "sg-btn",
      `sg-btn-${VARIANT_KIND[variant] || "primary"}`,
      `sg-btn-${SIZE_KIND[size] || "md"}`,
      isIconOnly ? "sg-btn-icon" : "",
      fullWidth ? "w-full" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Component to render - support link buttons
    const Component = href ? "a" : "button";

    // Props for the component
    const componentProps = {
      className: buttonClasses,
      disabled: disabled || loading,
      onClick: handleClick,
      "data-active": active ? "true" : undefined,
      "data-status": succeeded ? "success" : loading ? "loading" : undefined,
      "aria-label": ariaLabel || (isIconOnly ? "Button" : undefined),
      "aria-pressed": ariaPressed,
      "aria-expanded": ariaExpanded,
      "aria-busy": loading,
      ...accessibilityProps,
      ...props,
    };

    // Additional props for links
    if (href) {
      componentProps.href = href;
      componentProps.target = target;
      componentProps.rel =
        rel || (target === "_blank" ? "noopener noreferrer" : undefined);
      delete componentProps.disabled; // Links don't have disabled
      delete componentProps.type;
    } else {
      componentProps.type = type;
    }

    // The label keeps its box while loading or succeeded, so the button
    // never changes width; the status layer sits on top of it.
    const labelHidden = loading || succeeded;

    return (
      <Component ref={buttonRef} {...componentProps}>
        {loading && (
          <span className="sg-btn-status" data-status="loading">
            <span className="sg-btn-spinner" aria-hidden="true" />
            {loadingText && <span>{loadingText}</span>}
          </span>
        )}

        {succeeded && (
          <span className="sg-btn-status" data-status="success">
            <svg
              className="sg-btn-check"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3.5 8.4l2.9 2.9 6.1-6.6" />
            </svg>
            <span className="sr-only" role="status">
              {successText}
            </span>
          </span>
        )}

        <span
          className="sg-btn-label"
          data-hidden={labelHidden ? "true" : undefined}
          aria-hidden={labelHidden ? true : undefined}
        >
          {resolvedLeftIcon && (
            <span className="flex items-center">{resolvedLeftIcon}</span>
          )}
          {children !== undefined && children !== null && children !== "" && (
            <span>{children}</span>
          )}
          {resolvedRightIcon && (
            <span className="flex items-center">{resolvedRightIcon}</span>
          )}
        </span>
      </Component>
    );
  },
);

Button.displayName = "Button";

// Icon Button component for icon-only buttons (square, sized by `size`)
export const IconButton = React.memo(
  ({
    icon,
    variant = "primary",
    size = "md",
    className = "",
    ariaLabel,
    ...props
  }) => {
    // Ensure icon buttons always have an aria-label for accessibility
    if (!ariaLabel && !props["aria-label"]) {
      console.warn(
        "IconButton should have an ariaLabel prop for accessibility",
      );
    }

    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        ariaLabel={ariaLabel}
        leftIcon={icon}
        {...props}
      >
        {/* Intentionally empty to create an icon-only button */}
      </Button>
    );
  },
);

IconButton.displayName = "IconButton";

// Button Group for related buttons
export const ButtonGroup = React.memo(
  ({
    children,
    variant = "primary",
    size = "md",
    className = "",
    vertical = false,
    ...props
  }) => {
    // Apply styling to make buttons appear as a group
    const groupClasses = `
    inline-flex
    ${vertical ? "flex-col" : "flex-row"}
    ${className}
  `;

    // Clone children and add appropriate styling
    const enhancedChildren = React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) return child;

      let roundedClasses = "";

      if (vertical) {
        if (index === 0) roundedClasses = "rounded-t-md rounded-b-none";
        else if (index === React.Children.count(children) - 1)
          roundedClasses = "rounded-b-md rounded-t-none";
        else roundedClasses = "rounded-none";
      } else {
        if (index === 0) roundedClasses = "rounded-r-none rounded-l-md";
        else if (index === React.Children.count(children) - 1)
          roundedClasses = "rounded-l-none rounded-r-md";
        else roundedClasses = "rounded-none";
      }

      // Border adjustments for connecting buttons
      const borderClass = vertical
        ? index !== 0
          ? "border-t-0"
          : ""
        : index !== 0
          ? "border-l-0"
          : "";

      return React.cloneElement(child, {
        variant: child.props.variant || variant,
        size: child.props.size || size,
        className: `${child.props.className || ""} ${roundedClasses} ${borderClass} ${vertical ? "w-full" : ""}`,
      });
    });

    return (
      <div className={groupClasses} {...props}>
        {enhancedChildren}
      </div>
    );
  },
);

ButtonGroup.displayName = "ButtonGroup";

// Primary export
export { Button };
export default Button;
